import React from 'react'
import { useParams } from 'react-router-dom'
import { useGetRegionByIdQuery, useUpdateRegionMutation } from '../Service/regionsApi'
import { CircularProgress, Typography } from '@mui/material'
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { regionValues } from '../initialValues'
import { regionFields } from '../fields'
import { useGetCountriesQuery } from '../../Countries/Service/countriesApi'
import { Region } from '../Types/regionTypes'
import { RegionsRoutes } from '../../../Router/path'
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import ImageIcon from "@mui/icons-material/Image";
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types'
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout'
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard'
import { useGetRegionWithImagesQuery } from '../Service/extendedRegionsApi'
import UploadRegionImagesForm from '../Components/UploadRegionImagesForm'
import UploadedRegionImagesGallery from '../Components/UploadedRegionImagesGallery'

const EditRegion = () => {
    const regionId = parseInt(useParams<{ regionId: string }>().regionId!)
    const { data: regionData, isLoading: isRegionLoading, error: regionError } = useGetRegionByIdQuery(regionId, {
        skip: isNaN(regionId)
    })
    const { data: countries, isLoading: countriesLoading } = useGetCountriesQuery();
    const [updateRegion, updateStatus] = useUpdateRegionMutation()

    const {
        data: regionWithImagesData,
        isLoading: isImagesLoading,
        isError: isImagesError,
        refetch: refetchImages,
    } = useGetRegionWithImagesQuery(regionId, { skip: isNaN(regionId) });

    const entityForm = useEntityForm({
        initialData: regionData ?? regionValues,
        fields: regionFields.map((field) => {
            if (field.type === "autocomplete" && countries) {
                return {
                    ...field,
                    name: field.name as keyof Region,
                    options: countries.map((country) => ({
                        label: country.name,
                        value: country.id,
                    })),
                    loading: countriesLoading,
                };
            }
            return field;
        }),
        submitFn: (data) => updateRegion({ id: regionId, data }).unwrap(),
        redirectUrl: RegionsRoutes.root,
        title: 'Edytuj region',
        icon: <InfoIcon />,
        backUrl: RegionsRoutes.root,
        groupOrder: [
            FormRenderGroupTypes.basic,
            FormRenderGroupTypes.configuration,
        ],
        groupIcons: {
            Podstawowe: <InfoIcon sx={{ color: theme => theme.palette.primary.main }} />,
            Konfiguracja: <SettingsIcon sx={{ color: theme => theme.palette.primary.main }} />,
        },
        mutationStatus: updateStatus,
        showSuccessBeforeRedirect: true,
        redirectDelay: 2000,
    })
    if (isRegionLoading) {
        return (
            <Typography align="center" sx={{ mt: 4 }}>
                <CircularProgress />
            </Typography>
        );
    }

    if (regionError) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                Nie udało się załadować danych regionu.
            </Typography>
        );
    }

    return (
        <EntityFormLayout {...entityForm}>
            <AccordionCard title="Wgrane zdjęcia" icon={<ImageIcon />}>
                {regionWithImagesData?.images?.length ? (
                    <>
                        <UploadedRegionImagesGallery
                            images={regionWithImagesData.images}
                            isLoading={isImagesLoading}
                            isError={isImagesError}
                            regionId={regionId}
                            onDeleteSuccess={refetchImages}
                        />
                        <UploadRegionImagesForm regionId={regionId} onUploadSuccess={refetchImages} />
                    </>
                ) : (
                    <Typography color="text.secondary" component="div">
                        Nie wgrano jeszcze żadnych zdjęć.
                        <UploadRegionImagesForm regionId={regionId} onUploadSuccess={refetchImages} />
                    </Typography>
                )}
            </AccordionCard>
        </EntityFormLayout>
    )
}

export default EditRegion